/**
 * Reading time utilities for blogs and help articles.
 * Calculates estimated reading time based on content length.
 */

// Average reading speed (words per minute)
const WORDS_PER_MINUTE = 200;

// Average word length in characters (including spaces)
const AVG_CHARS_PER_WORD = 5;

/**
 * Calculate reading time in minutes from HTML content.
 * Strips HTML tags before counting.
 */
export function getReadingTime(htmlContent: string): number {
  if (!htmlContent) return 1;

  // Strip HTML tags
  const text = htmlContent.replace(/<[^>]*>/g, '');

  // Estimate word count from character length
  const wordCount = Math.ceil(text.length / AVG_CHARS_PER_WORD);

  // Calculate minutes, minimum 1 minute
  const minutes = Math.max(1, Math.ceil(wordCount / WORDS_PER_MINUTE));

  return minutes;
}

/**
 * Format reading time for display.
 * Returns localized string like "4 min read" or "4 分鐘閱讀"
 */
export function formatReadingTime(
  minutes: number,
  language: 'en' | 'zh' = 'en'
): string {
  if (language === 'zh') {
    return `${minutes} 分鐘閱讀`;
  }
  return `${minutes} min read`;
}

/**
 * Get formatted reading time directly from HTML content.
 * Convenience function combining getReadingTime and formatReadingTime.
 */
export function getFormattedReadingTime(
  htmlContent: string,
  language: 'en' | 'zh' = 'en'
): string {
  const minutes = getReadingTime(htmlContent);
  return formatReadingTime(minutes, language);
}
